/**
 * WordPress dependencies
 */
import { createRoot, useEffect } from '@wordpress/element';
import domReady from '@wordpress/dom-ready';

/**
 * External dependencies
 *
 */
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter as Router, useSearchParams } from 'react-router-dom';

const queryClient = new QueryClient({
	defaultOptions: { queries: { staleTime: 1000 * 60 * 5 } },
});

/**
 * Internal dependencies
 */
import CourseList from './components/CourseList';
import Filter from './components/Filter';
import { useWPEvents, useLocalEventsTaxonomies } from '@hooks';

/**
 * Main Component
 */
function Main() {
	const [searchParams, setSearchParams] = useSearchParams();

	const {
		data: courseData,
		isPending: isPendingCourse,
		isError: isErrorCourse,
		refetch: refetchCallback,
	} = useWPEvents({
		filter: {
			categories: searchParams.get('category')?.split(',') || [],
			statuses: searchParams.get('status')?.split(',') || [],
		},
	});

	const {
		data: taxonomiesData,
		isPending: isPendingTaxonomies,
		isError: isErrorTaxonomies,
	} = useLocalEventsTaxonomies();

	useEffect(() => {
		refetchCallback();
	}, [searchParams]);

	return (
		<>
			<div className="course-filter-wrapper">
				<h2>Filters</h2>
				{isPendingTaxonomies && <p>Loading filters...</p>}
				{isErrorTaxonomies && <p>Error loading filters</p>}
				{taxonomiesData && (
					<div>
						<h3>Categories</h3>
						<Filter
							type="category"
							values={taxonomiesData.categories}
						/>
						<h3>Status</h3>
						<Filter
							type="status"
							values={taxonomiesData.statuses.filter((status) => {
								// Exclude 'fully-booked' and 'planned' statuses
								return !['fully-booked', 'planned'].includes(
									status.slug
								);
							})}
						/>
					</div>
				)}
			</div>
			<CourseList data={courseData} isPending={isPendingCourse} />
		</>
	);
}

/**
 * Render block
 */
domReady(() => {
	createRoot(document.getElementById('course-list')).render(
		<Router>
			<QueryClientProvider client={queryClient}>
				<Main />
			</QueryClientProvider>
		</Router>
	);
});
